import { AxiosError, AxiosResponse } from 'axios';
import { LaravelErrorResponse, UnprocessableEntity } from './errors';
import { GTMEvent } from './gtm';

/**
 * Helper types
 */

// Data object returned by the API
type ApiV1Data<T = any> = {
  data: T;
  gtmEvents?: GTMEvent[];
};

// AxiosError object but with a non-undefined response
type AxiosErrorHasResponse<T = any> = AxiosError<T> & {
  response: AxiosResponse<T>;
}

type HasResponseWithStatus<T extends number> = {
  response: {
    status: T;
  }
}

/**
 * Response types
 */

// Generic API response type
export type ApiV1Response<T = any> = AxiosResponse<ApiV1Data<T>>;

/**
 * Error response types
 */

// Generic API error type. For all kinds of errors, including 500s and 422s
export type ApiV1Error = AxiosErrorHasResponse<LaravelErrorResponse>;

// API Validation error response
export type ApiV1ValidationError<T = any> =
  AxiosErrorHasResponse<UnprocessableEntity<T>>;

// CSRF API error response
export type ApiV1CSRFError =
  AxiosErrorHasResponse<LaravelErrorResponse> & HasResponseWithStatus<419>;

/**
 * Type guards
 */

// Is the object a laravel error object
export const isLaravelError = (obj: any): obj is ApiV1Error => {
  return obj?.response?.data?.message !== undefined;
};

// Is the object a laravel validation error object
export const isValidationError = <T = any>(obj: any): obj is ApiV1ValidationError<T> => {
  return obj?.response?.data?.errors !== undefined
    && isLaravelError(obj);
};

// Is the object a laravel CSRF error object
export const isCSRFError = (obj: any): obj is ApiV1CSRFError => {
  return obj?.response?.status === 419
    && isLaravelError(obj);
};
